import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchDePaulData } from '@/services/universityService';
import { MetricsState } from '@/types/metrics';
import { createMetricsState } from '@/utils/metricTransformations';
import { useMetricUpdates } from './useMetricUpdates';
import { toast } from "sonner";
import { calculateSimulatedScore } from '@/services/simulatorService';
import { useRanking } from '@/context/RankingContext';

function useMetrics() {
  const [originalMetrics, setOriginalMetrics] = useState<MetricsState | null>(null);
  const { 
    isSimulationMode, 
    setSimulatedScore, 
    setDepaulRank,
    currentMetrics,
    setCurrentMetrics
  } = useRanking();

  const { data: depaulData, isLoading: depaulLoading } = useQuery({
    queryKey: ['depaul'],
    queryFn: fetchDePaulData,
  });

  const updateMetricsMutation = useMetricUpdates();

  // Set initial metrics when data is loaded
  useEffect(() => {
    if (!depaulLoading && depaulData && !currentMetrics) {
      const metricsState = createMetricsState(depaulData);
      setCurrentMetrics(metricsState);
      setOriginalMetrics(JSON.parse(JSON.stringify(metricsState)));
    }
  }, [depaulData, depaulLoading, currentMetrics, setCurrentMetrics]);

  // Keep original metrics in sync with non-simulation state
  useEffect(() => {
    if (!isSimulationMode && currentMetrics) {
      setOriginalMetrics(JSON.parse(JSON.stringify(currentMetrics)));
    }
  }, [isSimulationMode, currentMetrics]);

  const handleMetricChange = async (label: string, value: number, weight?: string) => {
    if (!depaulData?.university || !currentMetrics) return;
    
    let dbField: string | undefined;
    const updatedMetrics = { ...currentMetrics };
    
    // Update the metric value in the current state
    Object.values(updatedMetrics).forEach(metricGroup => {
      const metricIndex = metricGroup.findIndex(m => 
        m.label === label && (!weight || m.weight === weight)
      );
      if (metricIndex !== -1) {
        dbField = metricGroup[metricIndex].dbField;
        metricGroup[metricIndex] = { ...metricGroup[metricIndex], value };
      }
    });

    if (!dbField) {
      console.error(`No dbField found for metric: ${label}`);
      return;
    }

    try {
      setCurrentMetrics(updatedMetrics);

      // Calculate simulated score
      const newScore = await calculateSimulatedScore(updatedMetrics);
      
      if (isSimulationMode) {
        // In simulation mode, just update the score without database changes
        setSimulatedScore(newScore);
      } else {
        // Only update database if not in simulation mode
        await updateMetricsMutation.mutateAsync({
          university: depaulData.university,
          metricName: dbField,
          value: value
        });
      }
    } catch (error) {
      if (originalMetrics) {
        setCurrentMetrics(originalMetrics);
        setSimulatedScore(null);
      }
      if (!isSimulationMode) {
        toast.error("Failed to update metric");
        console.error("Failed to update metric:", error);
      }
    }
  };

  const handleReset = () => {
    if (!originalMetrics) {
      toast.error("Cannot reset metrics - original data not available");
      return;
    }

    try {
      // Deep copy the original metrics
      const resetMetrics = JSON.parse(JSON.stringify(originalMetrics));
      
      // Reset the metrics state
      setCurrentMetrics(resetMetrics);
      setSimulatedScore(null);
      // Reset the rank to original value (165)
      setDepaulRank(165);
      
      // Show success message
      toast.success(isSimulationMode 
        ? "Simulation reset to original values" 
        : "Metrics reset to original values"
      );
    } catch (error) {
      console.error("Error resetting metrics:", error);
      toast.error("Failed to reset metrics");
    }
  };

  return {
    currentMetrics,
    handleMetricChange,
    handleReset,
    isLoading: depaulLoading,
    originalMetrics
  };
}

export default useMetrics;